// Plain-language words for a promotion (activePromotionsForCustomer), shared by
// the restaurant page's deal banners and the cart's progress bar so the two
// always describe a deal the same way.

const money = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "?";
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

const firstReward = (promo) => (promo?.rewards || [])[0] || null;

/** The order total a deal needs, or null when it has no spend threshold. */
export function dealThreshold(promo) {
  const c = (promo?.conditions || []).find((x) => x.conditionType === "SPEND_THRESHOLD");
  const n = parseFloat(c?.params?.minAmount);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** discount | freeItem | freeDelivery | loyalty — picks the banner's colour. */
export function dealKind(promo) {
  if (promo?.promotionType === "LOYALTY_REWARD") return "loyalty";
  const type = firstReward(promo)?.rewardType;
  if (type === "FREE_ITEM" || type === "CHEAPEST_ITEM_FREE") return "freeItem";
  if (type === "FREE_DELIVERY") return "freeDelivery";
  return "discount";
}

/** Big banner headline: "$10 OFF", "15% OFF", "Free French Fries". */
export function dealHeadline(promo) {
  const r = firstReward(promo);
  const p = r?.params || {};
  switch (r?.rewardType) {
    case "PERCENT_OFF": return `${money(p.percent)}% OFF`;
    case "AMOUNT_OFF": return `$${money(p.amount)} OFF`;
    case "FREE_DELIVERY": return "Free delivery";
    case "FREE_ITEM": return `Free ${p.title || "item"}`;
    case "CHEAPEST_ITEM_FREE": return "Buy one, get one";
    case "GENERATE_VOUCHER": return `$${money(p.voucherAmount)} voucher`;
    default: return promo?.name || "Deal";
  }
}

/** What you get, mid-sentence: "$10 off", "Free French Fries". */
export function dealReward(promo) {
  const r = firstReward(promo);
  const p = r?.params || {};
  switch (r?.rewardType) {
    case "PERCENT_OFF": return `${money(p.percent)}% off`;
    case "AMOUNT_OFF": return `$${money(p.amount)} off`;
    case "FREE_DELIVERY": return "Free delivery";
    case "FREE_ITEM": return `Free ${p.title || "item"}`;
    case "CHEAPEST_ITEM_FREE": return "Cheapest item free";
    case "GENERATE_VOUCHER": return `$${money(p.voucherAmount)} voucher`;
    default: return promo?.name || "this deal";
  }
}

/** A pill's few words: "15% off $50+", "Free French Fries $35+". */
export function dealShort(promo) {
  const threshold = dealThreshold(promo);
  return `${dealReward(promo)}${threshold ? ` $${money(threshold)}+` : ""}`;
}

/** The banner's one supporting line: "on orders of $30 or more". */
export function dealCondition(promo) {
  const parts = [];
  const threshold = dealThreshold(promo);
  const qty = (promo?.conditions || []).find((x) => x.conditionType === "REQUIRED_QTY");
  if (threshold) parts.push(`on orders of $${money(threshold)} or more`);
  else if (qty) parts.push(`when you buy ${qty.params?.qty ?? "?"} or more`);
  if (promo?.channel === "DELIVERY") parts.push("delivery only");
  if (promo?.channel === "PICKUP") parts.push("pickup only");
  if (parts.length) return parts.join(" · ");
  return promo?.description || "";
}

/** True when the customer types a code for it, not applied automatically. */
export const dealNeedsCode = (promo) => promo?.triggerType === "COUPON_CODE" && !!promo?.couponCode;

/**
 * Deals that can apply to this cart: catering deals only to a catering cart,
 * and a delivery-only / pickup-only deal only in that mode.
 */
export function dealsForCart(promos, { cateringCart = false, pickup = false } = {}) {
  return (promos || []).filter((p) => {
    if (p.promotionType === "LOYALTY_REWARD") return false;
    if (p.isCatering && !cateringCart) return false;
    if (!p.isCatering && cateringCart) return false;
    if (p.channel === "DELIVERY" && pickup) return false;
    if (p.channel === "PICKUP" && !pickup) return false;
    return true;
  });
}
